(function() {
  window.theme = window.theme || {};
  if (window.theme.productSiblingNavigationLoaded) return;
  window.theme.productSiblingNavigationLoaded = true;
  const productInfoSelector = 'product-info';
  const transitionDataSelector = '[data-product-sibling-page-data][data-enabled="true"]';
  const siblingLinkSelector = '.product__siblings a.sibling__link:not(.sibling__link--current)';
  const refreshSectionSelector = '[data-product-sibling-refresh-section]';
  const maximumSections = 5;
  const responseCache = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let requestController;
  let navigationSequence = 0;
  let sectionLimitWarningShown = false;

  function getHistoryState() {
    return history.state && typeof history.state === 'object' ? history.state : {};
  }

  function getScrollPosition() {
    return {x: window.scrollX, y: window.scrollY};
  }

  function getSectionWrapper(sectionId, root = document) {
    return root.getElementById(`shopify-section-${sectionId}`);
  }

  function getProductInfo() {
    const transitionData = document.querySelector(transitionDataSelector);
    return transitionData?.closest('.shopify-section')?.querySelector(productInfoSelector) || null;
  }

  function getTransitionData(productInfo = getProductInfo()) {
    return productInfo?.closest('.shopify-section')?.querySelector(transitionDataSelector) || null;
  }

  function getSectionIds(productInfo) {
    const mainSectionId = getTransitionData(productInfo)?.dataset.sectionId || productInfo.dataset.sectionId;
    const additionalSectionIds = [...document.querySelectorAll(refreshSectionSelector)]
      .map((marker) => marker.dataset.productSiblingRefreshSection)
      .filter((sectionId, index, sectionIds) => {
        return sectionId && sectionId !== mainSectionId && sectionIds.indexOf(sectionId) === index && getSectionWrapper(sectionId);
      });

    if (additionalSectionIds.length >= maximumSections && !sectionLimitWarningShown) {
      console.warn('Only the first four additional product sections will refresh during a color change.');
      sectionLimitWarningShown = true;
    }

    return [mainSectionId, ...additionalSectionIds.slice(0, maximumSections - 1)].filter(Boolean);
  }

  function setLoadingState(sectionIds, sourceLink, isLoading) {
    document.documentElement.classList.toggle('is-product-sibling-loading', isLoading);
    sourceLink?.classList.toggle('is-product-sibling-loading', isLoading);

    sectionIds.forEach((sectionId) => {
      const section = getSectionWrapper(sectionId);
      if (!section) return;

      section.classList.toggle('is-product-sibling-loading', isLoading);
      if (isLoading) {
        section.setAttribute('aria-busy', 'true');
      } else {
        section.removeAttribute('aria-busy');
      }
    });
  }

  function parseSection(sectionId, html) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const parsedSection = getSectionWrapper(sectionId, parsedDocument);

    if (parsedSection) return parsedSection;

    const currentSection = getSectionWrapper(sectionId);
    if (!currentSection) return null;

    const fallbackSection = currentSection.cloneNode(false);
    fallbackSection.innerHTML = html;
    return fallbackSection;
  }

  function getSectionReplacements(sectionIds, response) {
    return sectionIds.map((sectionId) => {
      if (typeof response[sectionId] !== 'string') {
        throw new Error(`Missing section response for ${sectionId}`);
      }

      const currentSection = getSectionWrapper(sectionId);
      const nextSection = parseSection(sectionId, response[sectionId]);
      if (!currentSection || !nextSection) {
        throw new Error(`Unable to render section ${sectionId}`);
      }

      return {sectionId, currentSection, nextSection};
    });
  }

  function setMetaContent(selector, value) {
    const element = document.head.querySelector(selector);
    if (element && value) element.setAttribute('content', value);
  }

  function updatePageMetadata() {
    const transitionData = getTransitionData();
    if (!transitionData) return;

    const canonicalUrl = transitionData.dataset.canonicalUrl;
    const pageTitle = transitionData.dataset.pageTitle;
    const socialTitle = transitionData.dataset.socialTitle;
    const description = transitionData.dataset.pageDescription;
    const socialImage = transitionData.dataset.socialImage;
    const canonicalLink = document.head.querySelector('link[rel="canonical"]');

    if (pageTitle) document.title = pageTitle;
    if (canonicalLink && canonicalUrl) canonicalLink.setAttribute('href', canonicalUrl);

    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[property="og:title"]', socialTitle);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[name="twitter:title"]', socialTitle);
    setMetaContent('meta[name="twitter:description"]', description);
    setMetaContent('meta[property="og:price:amount"]', transitionData.dataset.productPrice);
    setMetaContent('meta[property="og:price:currency"]', transitionData.dataset.currency);

    if (socialImage) {
      const absoluteImageUrl = new URL(socialImage, window.location.origin).href;
      setMetaContent('meta[property="og:image"]', absoluteImageUrl);
      setMetaContent('meta[property="og:image:secure_url"]', absoluteImageUrl.replace(/^http:/, 'https:'));
      setMetaContent('meta[property="og:image:width"]', transitionData.dataset.socialImageWidth);
      setMetaContent('meta[property="og:image:height"]', transitionData.dataset.socialImageHeight);
    }
  }

  function restoreScroll(position) {
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(position.x, position.y);

    requestAnimationFrame(() => {
      window.scrollTo(position.x, position.y);
      root.style.scrollBehavior = previousScrollBehavior;
    });
  }

  function updateHistory(targetUrl, mode, scrollPosition) {
    if (mode === 'push') {
      history.replaceState(
        {...getHistoryState(), productSiblingPage: true, productSiblingScroll: scrollPosition},
        '',
        window.location.href
      );
      history.pushState(
        {productSiblingPage: true, productSiblingScroll: scrollPosition},
        '',
        `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
      );
    }
  }

  function replaceSections(replacements, useFallbackAnimation) {
    replacements.forEach(({sectionId, currentSection, nextSection}) => {
      nextSection.classList.remove('is-product-sibling-loading');
      nextSection.removeAttribute('aria-busy');
      if (useFallbackAnimation) nextSection.classList.add('is-product-sibling-entering');
      currentSection.replaceWith(nextSection);
      nextSection.dispatchEvent(new CustomEvent('shopify:section:load', {bubbles: true, detail: {sectionId}}));
    });

    if (useFallbackAnimation) {
      requestAnimationFrame(() => {
        replacements.forEach(({nextSection}) => nextSection.classList.remove('is-product-sibling-entering'));
      });
    }
  }

  async function transitionSections(replacements) {
    if (document.startViewTransition && !reducedMotion.matches) {
      const transition = document.startViewTransition(() => replaceSections(replacements, false));
      await transition.updateCallbackDone;
      transition.finished.catch(() => {});
      return;
    }

    replaceSections(replacements, !reducedMotion.matches);
  }

  async function fetchSections(targetUrl, sectionIds) {
    const requestUrl = new URL(targetUrl.href);
    requestUrl.searchParams.set('sections', sectionIds.join(','));
    const cacheKey = requestUrl.href;

    requestController?.abort();
    if (responseCache.has(cacheKey)) return responseCache.get(cacheKey);

    requestController = new AbortController();

    const response = await fetch(requestUrl.href, {
      credentials: 'same-origin',
      headers: {'X-Requested-With': 'XMLHttpRequest'},
      signal: requestController.signal
    });

    if (!response.ok) throw new Error(`Product section request failed with ${response.status}`);

    const sectionResponse = await response.json();
    responseCache.set(cacheKey, sectionResponse);

    if (responseCache.size > 6) {
      responseCache.delete(responseCache.keys().next().value);
    }

    return sectionResponse;
  }

  async function navigateToSibling(targetUrl, options = {}) {
    const productInfo = getProductInfo();
    if (!productInfo) {
      window.location.assign(targetUrl.href);
      return;
    }

    const sectionIds = getSectionIds(productInfo);
    const scrollPosition = options.scrollPosition || getScrollPosition();
    const currentNavigation = ++navigationSequence;
    setLoadingState(sectionIds, options.sourceLink, true);

    try {
      const response = await fetchSections(targetUrl, sectionIds);
      if (currentNavigation !== navigationSequence) throw new DOMException('Navigation superseded', 'AbortError');
      const replacements = getSectionReplacements(sectionIds, response);
      await transitionSections(replacements);
      updateHistory(targetUrl, options.historyMode, scrollPosition);
      updatePageMetadata();
      restoreScroll(scrollPosition);

      if (options.restoreFocus) {
        requestAnimationFrame(() => {
          getProductInfo()?.querySelector('.product__siblings .sibling__link--current')?.focus({preventScroll: true});
        });
      }

      const nextProductInfo = getProductInfo();
      const nextTransitionData = getTransitionData(nextProductInfo);
      document.dispatchEvent(
        new CustomEvent('theme:product:sibling:change', {
          detail: {
            productId: nextTransitionData?.dataset.productId || nextProductInfo?.dataset.productId,
            sectionIds,
            url: targetUrl.href
          }
        })
      );
    } finally {
      if (currentNavigation === navigationSequence) {
        setLoadingState(sectionIds, options.sourceLink, false);
      }
    }
  }

  function initializeHistory() {
    if (!getProductInfo()) return;

    history.replaceState(
      {...getHistoryState(), productSiblingPage: true, productSiblingScroll: getScrollPosition()},
      '',
      window.location.href
    );
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(siblingLinkSelector);
    const productInfo = link?.closest(productInfoSelector);
    if (!link || !productInfo || !getTransitionData(productInfo) || window.Shopify?.designMode) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;

    const targetUrl = new URL(link.href, window.location.href);
    if (targetUrl.origin !== window.location.origin) return;

    event.preventDefault();
    const scrollPosition = getScrollPosition();

    navigateToSibling(targetUrl, {
      historyMode: 'push',
      restoreFocus: true,
      scrollPosition,
      sourceLink: link
    }).catch((error) => {
      if (error.name !== 'AbortError') window.location.assign(targetUrl.href);
    });
  });

  window.addEventListener('popstate', (event) => {
    if (!event.state?.productSiblingPage || !getProductInfo()) return;

    const targetUrl = new URL(window.location.href);
    const scrollPosition = event.state.productSiblingScroll || getScrollPosition();

    navigateToSibling(targetUrl, {historyMode: 'none', scrollPosition}).catch((error) => {
      if (error.name !== 'AbortError') window.location.reload();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHistory, {once: true});
  } else {
    initializeHistory();
  }
})();


